// Tab-scoped proof that THIS tab started a same-tab Google sign-in on /login.
//
// /socialAuth (googleAuthRedirect) stores a returned Google token as the
// session only for a tab that carries this mark, signed in or not (login CSRF,
// 2026-10-18: a crafted /socialAuth link signed a signed-out visitor in as the
// link's account). Why it began (2026-10-16): Account Settings' Connect Google popup opens the
// same Google flow to PROVE a second account. When that popup loses
// window.opener (its Settings tab was closed before Google finished, or a page
// in the sign-in sent an enforced Cross-Origin-Opener-Policy) it lands on
// /socialAuth like a same-tab return, and storing its token replaced the
// signed-in user with the Google account. Verified in Chromium, Firefox and
// WebKit on a production build.
//
// sessionStorage, not localStorage: it belongs to one tab and survives that
// tab's round trip through the API and Google, but another tab's attempt can
// never authorize this one. window.open copies the opener tab's
// sessionStorage into the popup: Account Settings' Connect Google clears the
// mark before opening (its popup must never sign in), and the exam-review
// drawer writes a fresh mark for its popup and clears it in its own tab right
// after opening (its popup may finish the visitor's sign-in). /login also
// sweeps it on a mount that is not finishing a return, and when Back restores
// it from the back/forward cache.
export const GOOGLE_SAME_TAB_SIGNIN_KEY = 'googleSameTabSignInAt';

// One Google round trip, generously: creating a Google account or a second
// factor can keep someone at Google for a while, and a signed-out student
// whose mark has expired is refused. Longer than login.jsx's 10-minute
// oauthReturnPath stash, which only picks the destination.
export const GOOGLE_SAME_TAB_SIGNIN_MAX_AGE_MS = 30 * 60 * 1000;

export const markGoogleSameTabSignIn = () => {
    try {
        window.sessionStorage.setItem(GOOGLE_SAME_TAB_SIGNIN_KEY, String(Date.now()));
    } catch (e) {
        // Storage blocked: the return then cannot complete. localStorage, which
        // holds the session itself, is blocked in the same browsers.
    }
};

export const clearGoogleSameTabSignIn = () => {
    try {
        window.sessionStorage.removeItem(GOOGLE_SAME_TAB_SIGNIN_KEY);
    } catch (e) {
        // Storage blocked: nothing was stored to clear.
    }
};

// Read and consume: true only for a mark written within one round trip.
export const takeGoogleSameTabSignIn = () => {
    let markedAt;
    try {
        markedAt = Number(window.sessionStorage.getItem(GOOGLE_SAME_TAB_SIGNIN_KEY));
    } catch (e) {
        return false;
    }
    clearGoogleSameTabSignIn();
    const age = Date.now() - markedAt;
    return markedAt > 0 && age >= 0 && age < GOOGLE_SAME_TAB_SIGNIN_MAX_AGE_MS;
};
